import { getCurrentLang } from "./Local";

export function flatPhoneNumber(number: string): string {
  return number.replace(/\D/g, "");
}

export const formatMoney = (amount: number): string =>
  new Intl.NumberFormat(`${getCurrentLang()}-CA`, {
    style: "currency",
    currency: "CAD",

    // These options are needed to round to whole numbers if that's what you want.
    // minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    maximumFractionDigits: 2, // (causes 2500.99 to be printed as $2,501)
  }).format(amount);

export const formatDate = (date: Date, locale: "en" | "fr"): string =>
  new Date(date).toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

export const formatArea = (area: number, locale: "en" | "fr"): string => {
  const unit = locale === "fr" ? "pieds carrés" : "sq ft";
  return `${area.toFixed(2)} ${unit}`;
};

export const formatWeight = (weight: number): string =>
  `${weight.toFixed(2)} lbs`;

export const formatDimension = (w: number, l: number, t: number): string =>
  `${w}" x ${l}" x ${t}"`;

export const formatDistance = (distanceInMeter: number): string => {
  const distanceInKm = (distanceInMeter / 1000).toFixed(2);
  return `${distanceInKm} km`;
};

export function printValues(obj: Record<string, unknown>): string[] {
  const values: string[] = [];
  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === "object") {
      printValues(obj[key] as Record<string, unknown>);
    } else {
      values.push(obj[key] as string);
    }
  });
  return values;
}

export const delay = async (ms: number | undefined): Promise<void> => {
  await new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
};

export const getBaseUrl = (): string => {
  const { protocol } = window.location;
  const host = window.location.hostname;
  return `${protocol}//${host}`;
};

export const youTubeLink = (): string =>
  "https://www.youtube.com/channel/UCOETzq4CMluyhH87DrwMW5g";

export const facebookLink = (): string =>
  "https://www.facebook.com/profile.php?id=61552975003426";

export const instagramLink = (): string => "https://www.instagram.com/pakville";

export const linkedinLink = (): string =>
  "https://www.linkedin.com/company/pakville";

export const validateEmail = (email: string): boolean => {
  const result =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.exec(
      String(email).toLowerCase(),
    );

  return result !== null;
};

export const calculateReadTime = (textContent: string): number => {
  if (textContent === undefined || textContent === "") {
    return 0;
  }
  const words = textContent.split(/\s+/).filter(Boolean).length;
  const wordsPerMinute = 250; // average reading speed
  return Math.ceil(words / wordsPerMinute);
};

export const isLargeScreen = (): boolean => window.innerWidth > 1200;

type TranslateFunction = (key: string) => string;

export const getCheckoutReturnUrl = (t: TranslateFunction): string => {
  let url = import.meta.env.VITE_SITE_URL as string;
  if (getCurrentLang() === "fr") {
    url += "/fr";
  }
  return `${url}/${t("_route.success")}`;
};

export const getReserveReturnUrl = (): string => {
  let url = import.meta.env.VITE_SITE_URL as string;
  if (getCurrentLang() === "fr") {
    url += "/fr";
  }
  return `${url}/success-reserve`;
};

export const isCompanyValid = (company: string): boolean =>
  company?.length <= 120;

export const getHomePath = (): string => {
  if (getCurrentLang() === "fr") {
    return "/fr";
  }
  return "/";
};

/**
 * Enhanced mobile detection that combines user agent and screen size
 * @param breakpoint - Optional screen width breakpoint (default: 768px)
 * @returns boolean - true if mobile device, false otherwise
 */
export const isMobile = (breakpoint = 768): boolean => {
  if (typeof window === "undefined") return false;

  const { userAgent } = navigator;
  const isMobileUserAgent =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent,
    );
  const isSmallScreen = window.innerWidth <= breakpoint;

  return isMobileUserAgent || isSmallScreen;
};

export const formatFormOptionValue = (val: string): string =>
  String(val).charAt(0).toUpperCase() +
  String(val).slice(1).replaceAll("_", " ");
